// ──────────────────────────────────────────────────────────────────────────
// ticketModel.js — capa de adaptación incidencia (back real) → ticket (board).
//
// El backend de incidencias maneja HOY 3 estados (abierta / procesando /
// cerrada) + flags de resolución por departamento (sac/administration/
// supervision). La ticketera quiere 5 columnas tipo Jira. Acá derivamos esas
// columnas SIN inventar datos: se calculan a partir de `status`, `resolved*`
// y la actividad real (comentarios). La columna "Esperando" es un refinamiento
// de front (no hay estado propio en el back) — ver reporte de gaps.
// ──────────────────────────────────────────────────────────────────────────

import {
  INCIDENT_TYPE_LABEL,
  DEPARTMENT_LABELS,
} from "../../../constants/incidents";

// Columnas del board (orden de flujo). `backStatus` es el estado real al que
// mapea para cuando exista un endpoint de cambio de estado de ticket.
export const TICKET_COLUMNS = [
  { key: "nuevo",       labelKey: "Nuevo",       tone: "error",   backStatus: "abierta" },
  { key: "en_proceso",  labelKey: "En proceso",  tone: "warning", backStatus: "procesando" },
  { key: "esperando",   labelKey: "Esperando",   tone: "info",    backStatus: "procesando", frontOnly: true },
  { key: "resuelto",    labelKey: "Resuelto",    tone: "success", backStatus: "cerrada" },
  { key: "cerrado",     labelKey: "Cerrado",     tone: "neutral", backStatus: "cerrada" },
];

export const SEVERITY_TONE = {
  critica: "error",
  alta: "warning",
  media: "info",
  baja: "neutral",
  // alias EN por si el back devuelve en inglés
  critical: "error",
  high: "warning",
  medium: "info",
  low: "neutral",
};

export const COLUMN_TONE = TICKET_COLUMNS.reduce((acc, c) => {
  acc[c.key] = c.tone;
  return acc;
}, {});

// Tonos de avatar rotando por hash del nombre, para variedad visual estable.
const AVATAR_TONES = ["brand", "accent", "success", "warning", "neutral"];
export function avatarToneFor(seed) {
  const s = String(seed || "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return AVATAR_TONES[Math.abs(h) % AVATAR_TONES.length];
}

// Estado "crudo" del back normalizado a uno de: abierta | procesando | cerrada.
export function rawStatus(incident) {
  if (!incident) return "abierta";
  if (incident.status) return incident.status;
  return incident.resolvedAt ? "cerrada" : "abierta";
}

// ¿La incidencia tiene actividad? (comentarios en cualquier depto o el flujo
// SAC). Sirve para distinguir "Nuevo" (sin tocar) de "En proceso".
function hasActivity(incident) {
  if (!incident) return false;
  const buckets = [
    incident.comments,
    incident.sac?.comments,
    incident.administration?.comments,
    incident.supervision?.comments,
  ];
  return buckets.some((b) => Array.isArray(b) && b.length > 0);
}

// Departamentos resueltos / requeridos.
export function departmentProgress(incident) {
  const required = incident?.requiredDepartments || ["sac"];
  const resolved = required.filter((d) => incident?.[d]?.resolved === true ||
    (d === "sac" && !!incident?.resolvedAt && !incident?.requiredDepartments));
  return { required, resolved, total: required.length, done: resolved.length };
}

// Deriva la COLUMNA del board a partir del estado real + flags.
// `localOverride` permite que el front (drag/select optimista) gane mientras
// no exista endpoint de cambio de estado de ticket.
export function columnFor(incident, localOverride) {
  if (localOverride) return localOverride;
  const st = rawStatus(incident);
  if (st === "cerrada") {
    // Cerrada de verdad = cerrado; pero si solo SAC marcó resuelto y aún hay
    // deptos pendientes, lo tratamos como "resuelto".
    const { done, total } = departmentProgress(incident);
    return done >= total && total > 0 ? "cerrado" : "resuelto";
  }
  if (st === "procesando") return "en_proceso";
  // abierta
  return hasActivity(incident) ? "en_proceso" : "nuevo";
}

// Conteo de comentarios visibles (todos los buckets).
export function commentCount(incident) {
  const buckets = [
    incident?.comments,
    incident?.sac?.comments,
    incident?.administration?.comments,
    incident?.supervision?.comments,
  ];
  return buckets.reduce((sum, b) => sum + (Array.isArray(b) ? b.length : 0), 0);
}

// Etiqueta de tipificación (tipo de incidencia).
export function typeLabel(incident) {
  return (
    INCIDENT_TYPE_LABEL[incident?.incidentType] ||
    incident?.incidentType ||
    null
  );
}

// Nombre de la empresa / cliente del paquete.
export function clientName(incident) {
  return (
    incident?.package?.senderInfo?.businessName ||
    incident?.package?.senderInfo?.companyName ||
    null
  );
}

// Nº de seguimiento del paquete.
export function followingNumber(incident) {
  return incident?.package?.followingNumber || null;
}

// Responsable principal (asignado). Devuelve { name, lastName } o null.
export function assignee(incident) {
  if (incident?.assignedUser?.name) return incident.assignedUser;
  if (incident?.package?.assignTo?.name) return incident.package.assignTo;
  return null;
}

// Área / departamento "principal" pendiente, para mostrar como tag.
export function primaryDepartment(incident) {
  const { required, resolved } = departmentProgress(incident);
  const pending = required.find((d) => !resolved.includes(d));
  const dept = pending || required[0] || "sac";
  return { key: dept, label: DEPARTMENT_LABELS[dept] || dept };
}

// SLA: el back no expone fecha de vencimiento. Derivamos una "edad" del ticket
// (tiempo desde createdAt) que sirve como señal de urgencia. NO es un SLA real
// — ver gap de backend en el reporte.
export function ticketAgeMinutes(incident) {
  if (!incident?.createdAt) return null;
  const ms = Date.now() - new Date(incident.createdAt).getTime();
  return Math.max(0, Math.round(ms / 60000));
}
